import {
  BlockContentValue,
  SectionContainer,
  SectionTitle,
  SectionTitleHeading,
  Slider,
  SliderSlide
} from "@silversea-ssc/ui-components-react";
import { FareCode } from "@silversea-ssc/web-fares";
import CruiseItinerary from "src/page-components/cruise/itinerary/CruiseItinerary";
import {
  buildItineraryWithExcursions,
  useRemoteGetExcursions
} from "src/page-components/cruise/itinerary/Utils";
import CruiseSegmentCard from "components/CruiseSegmentCard/CruiseSegmentCard";
import CruiseSegmentSummary from "components/CruiseSegmentSummary/CruiseSegmentSummary";
import React, { ReactElement, useCallback, useMemo, useState } from "react";
import { isPresent } from "src/utils/checks";
import { useIsTabletOrDesktop } from "src/utils/useIsTabletOrDesktop";
import {
  ImageWithAltFragment,
  SpecialVoyageSegmentFragment,
  SvCruiseSegmentFragment
} from "types/graphql-types";
import { Record } from "effect";

type Props = {
  title?: string | ReactElement;
  cardBackgroundColor: "titanium" | "destinationColor";
  cruiseSegments: SvCruiseSegmentFragment[];
  mapSegments?: (SpecialVoyageSegmentFragment | undefined)[];
  defaultPortImage?: ImageWithAltFragment;
  shorexesActiveOnFares: FareCode[];
  itineraryDisclaimer: BlockContentValue | undefined;
};

const SpecialVoyageItinerary: React.FC<Props> = ({
  title,
  cardBackgroundColor,
  cruiseSegments,
  mapSegments,
  defaultPortImage,
  shorexesActiveOnFares,
  itineraryDisclaimer
}) => {
  const [activeSegment, setActiveSegment] = useState<SvCruiseSegmentFragment>(
    cruiseSegments[0]
  );
  const activeItinerary = activeSegment.data?.itinerary?.filter(isPresent);

  const enhancedSegmentsInfo = useMemo(
    () =>
      (mapSegments || []).filter(isPresent).reduce(
        (acc, x) => {
          const cruiseId = x?.cruise?._id;
          if (cruiseId) acc[cruiseId] = x;

          return acc;
        },
        {} as Record<string, SpecialVoyageSegmentFragment>
      ),
    [mapSegments]
  );

  const segmentMap =
    enhancedSegmentsInfo[activeSegment?._id || ""]?.map ||
    activeSegment?.data?.map;
  const activeSegmentTitle =
    enhancedSegmentsInfo[activeSegment?._id || ""]?.title?.localized;

  const activeSegmentIsFirst = cruiseSegments.indexOf(activeSegment) == 0;
  const activeSegmentIsLast =
    cruiseSegments.indexOf(activeSegment) == cruiseSegments.length - 1;

  const portCodes = activeItinerary
    ?.map((i) => i?.port?._id)
    .filter(isPresent)
    .map((portId) => portId.replace(/drafts\./, ""));

  const { data: excursionsData } = useRemoteGetExcursions(
    activeSegment?.data?.cruiseCode,
    portCodes
  );

  const { shorexes, confirmedExcursions } = excursionsData || {
    shorexes: [],
    confirmedExcursions: []
  };

  const itineraryWithExcursions = useMemo(() => {
    if (activeItinerary) {
      return buildItineraryWithExcursions(
        activeItinerary,
        shorexes,
        confirmedExcursions
      );
    } else return [];
  }, [confirmedExcursions, activeItinerary, shorexes]);

  const isTabletOrDesktop = useIsTabletOrDesktop();
  const changeActiveSegmentIfMobile = useCallback(
    (slideNumber: number) => {
      if (!isTabletOrDesktop) {
        const targetSegment = cruiseSegments[slideNumber];
        setActiveSegment(targetSegment);
      }
    },
    [cruiseSegments, isTabletOrDesktop]
  );

  return (
    <React.Fragment>
      <SectionContainer
        backgroundColor="neutral01"
        spacingTop="regular"
        spacingBottom="condensed"
        dataContentName="cruise_segment_section"
      >
        {title && (
          <SectionTitle variant="wide">
            <SectionTitleHeading>{title}</SectionTitleHeading>
          </SectionTitle>
        )}
        <Slider
          variantColor="white"
          onSlideChange={changeActiveSegmentIfMobile}
        >
          {cruiseSegments?.length > 0 &&
            cruiseSegments.map((c) => {
              const cruiseCode = c.data?.cruiseCode;
              if (!cruiseCode) return null;
              const enhancedSegmentInfo = enhancedSegmentsInfo[c._id || ""];
              return (
                <SliderSlide key={cruiseCode}>
                  <CruiseSegmentCard
                    backgroundColor={cardBackgroundColor}
                    segment={c}
                    active={activeSegment?._id == c._id}
                    onClick={setActiveSegment}
                    title={enhancedSegmentInfo?.title?.localized}
                  />
                </SliderSlide>
              );
            })}
        </Slider>
      </SectionContainer>
      {activeSegment && (
        <CruiseSegmentSummary
          segment={activeSegment}
          map={segmentMap}
          title={activeSegmentTitle}
        />
      )}
      <SectionContainer
        backgroundColor="white"
        spacingTop="regular"
        spacingBottom="regular"
      >
        {activeSegment && (
          <CruiseItinerary
            itinerary={itineraryWithExcursions}
            hideShorexesOnFirstDay={activeSegmentIsFirst}
            hideShorexesOnLastDay={activeSegmentIsLast}
            defaultPortImage={defaultPortImage}
            preItinerary={[]}
            postItinerary={[]}
            shorexesActiveOnFares={shorexesActiveOnFares}
            itineraryDisclaimer={itineraryDisclaimer}
          />
        )}
      </SectionContainer>
    </React.Fragment>
  );
};

export default SpecialVoyageItinerary;
